import React, { useEffect, useRef, useMemo } from 'react'
import { View, ImageBackground, Text } from 'react-native'
import { useSelector, useDispatch } from 'APP/Hooks/redux'
import I18n from 'APP/I18n'

// Actions
import PatientActions from 'APP/Redux/PatientRedux'
import { familyActions } from 'APP/Store/Family'

// Components
import NoticeBarContainer from 'APP/Components/NoticeBarContainer'
import DialogueBranding from 'APP/Components/DialogueBranding'
import Button from 'APP/Converse/Button'
import Alert from 'APP/Converse/Alert'
import { ResponsiveView } from 'APP/Converse/Layout'

// Styling
import HealthCard from 'APP/Images/health-card.svg'
import Styles, { StyledButtonContainerView } from './style'
import { Colors } from 'APP/Themes'
import { useFilePicker } from 'APP/Hooks/FilePicker'
import { isWeb } from 'APP/Helpers/checkPlatform'
import { useRoute } from '@react-navigation/native'
import { selectIsAppOnline } from 'APP/Sagas/AppSessionSagas'

/*

Note: This container is referenced by the following routes. I don't like it.

  - updateDependentPhotoId
  - updatePhotoId

*/

// TODO: Eradicate and replace this splodge of a screen with something that makes sense
const PhotoIdScreen = () => {
  const route = useRoute()
  const inputFile = useRef(null)
  const dispatch = useDispatch()

  //Store
  const idCardUrl = useSelector((state) => state.patient.idCardUrl || state.patient.idCardLocalPath)
  const patientRequestRunning = useSelector((state) => state.patient.requestRunning)
  const familyRequestRunning = useSelector((state) => state.family.requestRunning)
  const patientRequestError = useSelector((state) => state.patient.error)
  const isConnected = useSelector(selectIsAppOnline)

  //Derived state
  const routeName = route?.name
  const childProfile = route?.params?.childProfile

  const imageUri = useMemo(() => {
    if (routeName === 'updateDependentPhotoId' && childProfile?.healthcardUrl) {
      return childProfile.healthcardUrl
    } else if (routeName === 'updatePhotoId' && idCardUrl) {
      return idCardUrl
    }
  }, [childProfile, idCardUrl, routeName])

  const setIdCard = (fileObj) => {
    if (routeName === 'updateDependentPhotoId') {
      const { childProfile } = route?.params || {}
      const { id, dateOfBirth, familyName, givenName, preferredName } = childProfile
      dispatch(
        familyActions.updateChildProfileRequest({
          childId: id,
          childData: {
            dateOfBirth,
            familyName,
            givenName,
            preferredName,
          },
          fileObj,
        })
      )
    } else {
      dispatch(PatientActions.patientIdCardUpdateRequest(fileObj))
    }
  }

  const { openFilePicker, onFileChangeSubmit } = useFilePicker({
    submitCallback: setIdCard,
    fileInputRef: inputFile,
  })

  useEffect(() => {
    if (patientRequestError) {
      // Used to lower interference with spinner overlay
      setTimeout(
        () =>
          Alert.alert(I18n.t('PhotoIdScreen.error.title'), I18n.t('PhotoIdScreen.error.body'), [
            { text: I18n.t('PhotoIdScreen.error.ok') },
          ]),
        100
      )
    }
  }, [patientRequestError])

  const onFileChanged = (e) => {
    onFileChangeSubmit(e)
  }

  const editChild = routeName === 'updateDependentPhotoId'
  const submitRequestRunning = editChild ? familyRequestRunning : patientRequestRunning
  const photoTitle = imageUri ? 'PhotoIdScreen.editButton' : 'PhotoIdScreen.initialButton'

  return (
    <NoticeBarContainer>
      {isWeb() && (
        <input
          type="file"
          accept="image/*,.pdf"
          ref={inputFile}
          style={{ display: 'none' }}
          onChange={onFileChanged}
        />
      )}
      <ResponsiveView style={Styles.container}>
        {imageUri ? (
          <ImageBackground
            style={Styles.previewContainer}
            imageStyle={Styles.previewImage}
            source={{ uri: imageUri }}
            testID="HEALTH_CARD_IMAGE"
          />
        ) : (
          <View>
            <View style={Styles.placeholderImage}>
              <HealthCard fillLine={Colors.text} fillLineInverted={Colors.textInverted} />
            </View>
            <Text style={[Styles.sectionText, Styles.cameraSection]}>
              {editChild ? I18n.t('PhotoIdScreen.bodyChild') : I18n.t('PhotoIdScreen.body')}
            </Text>
          </View>
        )}
        <StyledButtonContainerView>
          <Button
            variant={imageUri ? 'secondary' : 'primary'}
            title={I18n.t(photoTitle)}
            widthVariant="full"
            onPress={openFilePicker}
            analyticsName={photoTitle}
            isLoading={submitRequestRunning}
            disabled={!isConnected || submitRequestRunning}
            testID="DEPENDENT_PHOTO_SCREEN_SELECT_PHOTO_BUTTON"
          />
        </StyledButtonContainerView>
        <DialogueBranding />
      </ResponsiveView>
    </NoticeBarContainer>
  )
}

export default PhotoIdScreen
